import { action, comparer, computed, makeObservable, observable, override, runInAction } from 'mobx'
import { getValue } from 'mobx-async'
import { createElement } from 'react'

import { CasesPredefinedFilterType } from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { collectTaxonomyNodeLabels, getTaxonomyTasks } from '@avantstay/case-taxonomy'
import { SortDirection } from '@avantstay/data-grid'

import { FiltersForPreview, FilterStore } from '@new/__global/components/FiltersFramework'
import {
  CaseFilterIds,
  casesFiltersConfig,
} from '@new/domains/operations/screens/CasesV2/utils/config'
import {
  collectTaxonomyNodeIdsByLevel,
  TaxonomyFilterLevel,
} from '@new/domains/operations/screens/CasesV2/utils/taxonomyFilterOptions'
import { parseLabel } from '@new/domains/operations/utils/parseLabel'

import { UiStore } from '@/_new/__global/stores/UiStore'
import { ROUTES } from '@routes'

import { SetQuickFilterProps } from '../CasesV2.types'
import { TruncatedListPreview } from '../components/PreviewFilterFormatter/TruncatedListPreview'

interface ColumnTaxonomyPair {
  legacyFilterId: CaseFilterIds
  level: TaxonomyFilterLevel
}

const COLUMN_TAXONOMY_PAIRS: ColumnTaxonomyPair[] = [
  { legacyFilterId: CaseFilterIds.RecordType, level: TaxonomyFilterLevel.Tasks },
  { legacyFilterId: CaseFilterIds.Type, level: TaxonomyFilterLevel.ServiceTypes },
  { legacyFilterId: CaseFilterIds.Category, level: TaxonomyFilterLevel.Categories },
  { legacyFilterId: CaseFilterIds.Subcategory, level: TaxonomyFilterLevel.Subcategories },
]

interface ApplyColumnTaxonomyFilterProps {
  legacyFilterId: CaseFilterIds
  level: TaxonomyFilterLevel
  legacyValues?: string[]
  taxonomyValues?: string[]
}

export class _CasesFiltersStore extends FilterStore {
  constructor() {
    super(casesFiltersConfig, [
      ROUTES.operationCases.path,
      ROUTES.operationCasesTapeChart.path,
      ROUTES.operationExpenses.path,
    ])

    makeObservable<
      _CasesFiltersStore,
      'taxonomyTasks' | 'taxonomyNodeIdsByLevel' | 'taxonomyNodeLabelById'
    >(this, {
      taxonomyTasks: computed,
      taxonomyNodeIdsByLevel: computed,
      taxonomyNodeLabelById: computed,
      taxonomyNodeIds: computed,
      applyColumnTaxonomyFilter: action.bound,
      filtersForPreview: override,
      quickFilterType: observable,
      quickFilterDate: observable,
      sortBy: computed,
      sortOrder: computed,
      page: computed,
      pageSize: computed,
      priorities: computed,
      recordTypes: computed,
      types: computed,
      categories: computed,
      subcategories: computed,
      taxonomyRecordTypes: computed,
      taxonomyTypes: computed,
      taxonomyCategories: computed,
      taxonomySubcategories: computed,
      shouldShowLegacyFilterOptions: computed,
      shouldShowTaxonomyFilterOptions: computed,
      dueDate: computed,
      regions: computed,
      status: computed,
      caseOwners: computed,
      caseVendors: computed,
      inStayGuestIssue: computed,
      recurring: computed,
      airbnbTripIssue: computed,
      hasExpense: computed,
      createdAt: computed,
      createdBy: computed,
      properties: computed,
      aiGenerated: computed,
      quickFilter: computed,
      assignedFO: computed,
      photoCompletion: computed,
      guestReadinessStatuses: computed,
      setSortOrder: action.bound,
      setQuickFilter: action.bound,
      setFilterValue: override,
      clearFilter: override,
      clearAllFilters: override,
      clearSidebarFilters: override,
      propertyStatuses: computed,
    })
  }

  quickFilterType: CasesPredefinedFilterType | undefined = undefined
  quickFilterDate: string | undefined = undefined

  get sortBy() {
    return this.computedRouteParams[CaseFilterIds.SortBy].get()
  }

  get sortOrder() {
    return this.computedRouteParams[CaseFilterIds.SortDirection].get()
  }

  get page() {
    return Number(this.computedRouteParams[CaseFilterIds.Page].get())
  }

  get pageSize() {
    return Number(this.computedRouteParams[CaseFilterIds.PageSize].get())
  }

  get priorities() {
    return this.computedRouteParams[CaseFilterIds.Priority]?.get()
  }

  get recordTypes() {
    return this.computedRouteParams[CaseFilterIds.RecordType]?.get()
  }

  get types() {
    return this.computedRouteParams[CaseFilterIds.Type]?.get()
  }

  get categories() {
    return this.computedRouteParams[CaseFilterIds.Category]?.get()
  }

  get subcategories() {
    return this.computedRouteParams[CaseFilterIds.Subcategory]?.get()
  }

  private get taxonomyTasks() {
    return getValue(getTaxonomyTasks()) ?? []
  }

  private get taxonomyNodeIdsByLevel() {
    return collectTaxonomyNodeIdsByLevel(this.taxonomyTasks)
  }

  private get taxonomyNodeLabelById() {
    return collectTaxonomyNodeLabels(this.taxonomyTasks)
  }

  get taxonomyNodeIds(): string[] | undefined {
    return this.computedRouteParams[CaseFilterIds.TaxonomyNodes]?.get()
  }

  private taxonomySelectionAtLevel(level: TaxonomyFilterLevel): string[] | undefined {
    const levelNodeIds = this.taxonomyNodeIdsByLevel[level]
    const selection = this.taxonomyNodeIds?.filter(nodeId => levelNodeIds.has(nodeId))
    return selection?.length ? selection : undefined
  }

  get taxonomyRecordTypes() {
    return this.taxonomySelectionAtLevel(TaxonomyFilterLevel.Tasks)
  }

  get taxonomyTypes() {
    return this.taxonomySelectionAtLevel(TaxonomyFilterLevel.ServiceTypes)
  }

  get taxonomyCategories() {
    return this.taxonomySelectionAtLevel(TaxonomyFilterLevel.Categories)
  }

  get taxonomySubcategories() {
    return this.taxonomySelectionAtLevel(TaxonomyFilterLevel.Subcategories)
  }

  // The two taxonomies have no id mapping, so a record-type selection in one
  // family hides the other family's options on the downstream column filters.
  get shouldShowLegacyFilterOptions() {
    return !this.taxonomyRecordTypes?.length || !!this.recordTypes?.length
  }

  get shouldShowTaxonomyFilterOptions() {
    return !this.recordTypes?.length || !!this.taxonomyRecordTypes?.length
  }

  applyColumnTaxonomyFilter({
    legacyFilterId,
    level,
    legacyValues,
    taxonomyValues,
  }: ApplyColumnTaxonomyFilterProps) {
    const levelNodeIds = this.taxonomyNodeIdsByLevel[level]
    const otherLevelsSelection = (this.taxonomyNodeIds ?? []).filter(
      nodeId => !levelNodeIds.has(nodeId),
    )
    const mergedSelection = [...otherLevelsSelection, ...(taxonomyValues ?? [])]

    this.applyFilters({
      [legacyFilterId]: legacyValues,
      [CaseFilterIds.TaxonomyNodes]: mergedSelection.length ? mergedSelection : undefined,
    })
  }

  get filtersForPreview(): FiltersForPreview[] {
    const pairsByLegacyId = new Map(
      COLUMN_TAXONOMY_PAIRS.map(pair => [String(pair.legacyFilterId), pair]),
    )
    const combinedLegacyIds = new Set<string>()

    const entries = super.filtersForPreview.flatMap(entry => {
      const pair = pairsByLegacyId.get(entry.id)
      if (!pair) return [entry]

      combinedLegacyIds.add(entry.id)
      return [this.buildColumnTaxonomyPreview(pair, entry.label)]
    })

    COLUMN_TAXONOMY_PAIRS.forEach(pair => {
      const hasTaxonomyOnlySelection =
        !combinedLegacyIds.has(pair.legacyFilterId) && !!this.taxonomySelectionAtLevel(pair.level)

      if (hasTaxonomyOnlySelection) {
        entries.push(this.buildColumnTaxonomyPreview(pair))
      }
    })

    return entries
  }

  private buildColumnTaxonomyPreview(
    { legacyFilterId, level }: ColumnTaxonomyPair,
    label?: string,
  ): FiltersForPreview {
    const legacyValues: string[] = this.computedRouteParams[legacyFilterId]?.get() ?? []
    const taxonomyNames = (this.taxonomySelectionAtLevel(level) ?? [])
      .map(nodeId => this.taxonomyNodeLabelById.get(nodeId))
      .filter((name): name is string => Boolean(name))
    const names = [...legacyValues.map(value => parseLabel(value)), ...taxonomyNames]

    return {
      id: legacyFilterId,
      label: label ?? this.filtersConfig.find(({ id }) => id === legacyFilterId)?.label,
      filterPreviewNoCapitalization: true,
      filterPreviewFormatter: () => createElement(TruncatedListPreview, { names }),
      onClear: () =>
        this.applyColumnTaxonomyFilter({
          legacyFilterId,
          level,
          legacyValues: undefined,
          taxonomyValues: undefined,
        }),
    }
  }

  get dueDate() {
    return this.computedRouteParams[CaseFilterIds.DueDate]?.get()
  }

  get regions() {
    return this.computedRouteParams[CaseFilterIds.Regions]?.get()
  }

  get status() {
    return this.computedRouteParams[CaseFilterIds.Status]?.get()
  }

  get caseOwners() {
    return this.computedRouteParams[CaseFilterIds.CaseOwner]?.get()
  }

  get caseVendors() {
    return this.computedRouteParams[CaseFilterIds.CaseVendor]?.get()
  }

  get inStayGuestIssue() {
    return this.computedRouteParams[CaseFilterIds.InStayGuestIssue]?.get()
  }

  get recurring() {
    return this.computedRouteParams[CaseFilterIds.Recurring]?.get()
  }

  get airbnbTripIssue() {
    return this.computedRouteParams[CaseFilterIds.AirbnbTripIssue]?.get()
  }

  get hasExpense() {
    return this.computedRouteParams[CaseFilterIds.HasExpense]?.get()
  }

  get createdAt() {
    return this.computedRouteParams[CaseFilterIds.CreateDate]?.get()
  }

  get createdBy() {
    return this.computedRouteParams[CaseFilterIds.CreatedBy]?.get()
  }

  get properties() {
    return this.computedRouteParams[CaseFilterIds.Property]?.get()
  }

  get aiGenerated() {
    return this.computedRouteParams[CaseFilterIds.AiGenerated]?.get()
  }

  get quickFilter() {
    return this.computedRouteParams[CaseFilterIds.QuickFilter]?.get()
  }

  get assignedFO() {
    return this.computedRouteParams[CaseFilterIds.AssignedFO]?.get()
  }

  get photoCompletion() {
    return this.computedRouteParams[CaseFilterIds.PhotoCompletion]?.get()
  }

  get guestReadinessStatuses() {
    return this.computedRouteParams[CaseFilterIds.GuestReadinessStatus]?.get()
  }

  get propertyStatuses() {
    return this.computedRouteParams[CaseFilterIds.PropertyStatus]?.get()
  }

  setFilterValue(id: string, value: unknown) {
    const param = this.computedRouteParams[id]
    const defaultValue = this.filtersConfig.find(filter => filter.id === id)?.defaultValue
    const changed = !comparer.structural(param?.get(), value ?? defaultValue)
    const cleanParams =
      id !== CaseFilterIds.Page && changed
        ? [this.computedRouteParams[CaseFilterIds.Page]]
        : undefined
    param?.push(value, { cleanParams })
  }

  clearFilter(id: string) {
    this.setFilterValue(id, undefined)
  }

  clearAllFilters() {
    this.quickFilterType = undefined
    this.quickFilterDate = undefined
    super.clearAllFilters()
  }

  clearSidebarFilters() {
    this.quickFilterType = undefined
    this.quickFilterDate = undefined
    super.clearAllFilters()
    UiStore.setShouldDisplayFilterSideBar(false)
  }

  setSortOrder(sortByKey: string, sortOrder: SortDirection) {
    const newSortByKey = sortOrder === SortDirection.None ? undefined : sortByKey
    const newSortDirection = sortOrder === SortDirection.None ? undefined : sortOrder
    this.setFilterValue(CaseFilterIds.SortBy, newSortByKey)
    this.setFilterValue(CaseFilterIds.SortDirection, newSortDirection)
  }

  setQuickFilter(
    filters: SetQuickFilterProps,
    // this is used when we open direct url with params
    skipResettingParams?: boolean,
  ) {
    runInAction(() => {
      this.quickFilterType = filters.quickFilterType
      this.quickFilterDate = filters.quickFilterDate
      this.applyFilters({
        [CaseFilterIds.SortBy]: filters?.sortBy,
        [CaseFilterIds.SortDirection]: filters?.sortOrder,
      })

      if (!skipResettingParams) {
        this.applyFilters({
          [CaseFilterIds.InStayGuestIssue]: undefined,
          [CaseFilterIds.Recurring]: undefined,
          [CaseFilterIds.Priority]: filters?.priorities,
          [CaseFilterIds.Category]: filters?.categories,
          [CaseFilterIds.Type]: filters?.types,
          [CaseFilterIds.Status]: filters?.status,
          [CaseFilterIds.DueDate]: filters?.dueDate,
          [CaseFilterIds.CreateDate]: filters?.createdAt,
        })
      }
    })
  }
}

export const casesFiltersStore = new _CasesFiltersStore()
